'use client';

import { c as _c } from "react/compiler-runtime";
import './alert.scss';
import type { ComponentType, Dispatch, ReactNode, SetStateAction } from 'react';
import { type JSX, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useIntersection from 'react-use/lib/useIntersection';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.ts';
import { TrackError } from '@sola/tracking/components/datalayer.tsx';
import { ButtonStyle, type ButtonStyleProps } from '../../atoms/button-style/button-style.tsx';
import { CloseButton } from '../../atoms/close-button/close-button.tsx';
import { LinkStyle } from '../../atoms/link-style/link-style.tsx';
import { cn } from '../../utils/helpers.ts';
import useReducedMotion from '../../utils/hooks/useReducedMotion.ts';
const alertDismiss = (setDismissed: Dispatch<SetStateAction<boolean>>, callback?: () => void) => {
  setDismissed(true);
  if (callback) {
    callback();
  }
};
export interface AlertProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title' | 'children'> {
  variant?: DisplayPurposeProps['variants'];
  title?: ReactNode;
  className?: string;
  message?: ReactNode;
  middleColumn?: ReactNode;
  footer?: ReactNode;
  /**
   * This should either be an anchor or button element that will be a child of <LinkStyle>
   * with the exception of using isFocus, which will use <ButtonStyle>
   */
  action?: JSX.Element;
  buttonAction?: JSX.Element;
  controlledTrigger?: ComponentType<{
    children: JSX.Element;
  }>;
  onClose?: () => void;
  noIcon?: boolean;
  dismissible?: boolean;
  closable?: boolean;
  scrollMobileOnly?: boolean;
  scrollIntoView?: boolean;
  scrollBehavior?: 'auto' | 'instant' | 'smooth';
  scrollPosition?: 'center' | 'end' | 'nearest' | 'start';
  mobileCentered?: boolean;
}

/**
 * Component for rendering alerts, which maintains its own dismissed state.
 */
export const Alert: React.FC<AlertProps> = t0 => {
  const $ = _c(71);
  const {
    variant: t1,
    className,
    title,
    message,
    middleColumn,
    footer,
    action,
    buttonAction,
    controlledTrigger: ControlledTrigger,
    onClose,
    noIcon,
    dismissible: t2,
    closable: t3,
    scrollMobileOnly: t4,
    scrollIntoView: t5,
    scrollBehavior: t6,
    scrollPosition: t7,
    mobileCentered
  } = t0;
  const variant = t1 === undefined ? "neutral" : t1;
  const dismissible = t2 === undefined ? false : t2;
  const closable = t3 === undefined ? false : t3;
  const scrollMobileOnly = t4 === undefined ? false : t4;
  const scrollIntoView = t5 === undefined ? false : t5;
  const scrollBehavior = t6 === undefined ? "smooth" : t6;
  const scrollPosition = t7 === undefined ? "start" : t7;
  let t8;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["core"];
    $[0] = t8;
  } else {
    t8 = $[0];
  }
  const {
    t
  } = useTranslation(t8);
  const [dismissed, setDismissed] = useState(false);
  const [hasSeen, setHasSeen] = useState(false);
  const alertRef = useRef(null);
  const {
    intersectionRatio
  } = useIntersection(alertRef as React.RefObject<HTMLElement>, {
    root: null,
    rootMargin: "0px",
    threshold: 1
  }) ?? {};
  const reducedMotion = useReducedMotion();
  const hasCloseButton = closable || dismissible;
  let t9;
  if ($[1] !== buttonAction || $[2] !== variant) {
    t9 = buttonAction ? <ButtonStyle purpose={variant as Exclude<ButtonStyleProps['purpose'], 'invert'>} size="md" className="Alert__button-action inline-block w-full md:w-fit h-fit">{buttonAction}</ButtonStyle> : undefined;
    $[1] = buttonAction;
    $[2] = variant;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const styledButtonAction = t9;
  let t10;
  if ($[4] !== hasSeen || $[5] !== intersectionRatio || $[6] !== reducedMotion || $[7] !== scrollBehavior || $[8] !== scrollIntoView || $[9] !== scrollMobileOnly || $[10] !== scrollPosition) {
    t10 = () => {
      if (!scrollIntoView || scrollMobileOnly && window.innerWidth >= 768) {
        return;
      }
      if (alertRef.current && !hasSeen && intersectionRatio !== 0) {
        alertRef.current?.scrollIntoView({
          behavior: reducedMotion ? "auto" : scrollBehavior,
          block: scrollPosition
        });
        setHasSeen(true);
      }
    };
    $[4] = hasSeen;
    $[5] = intersectionRatio;
    $[6] = reducedMotion;
    $[7] = scrollBehavior;
    $[8] = scrollIntoView;
    $[9] = scrollMobileOnly;
    $[10] = scrollPosition;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  useEffect(t10, t11);
  if (dismissed) {
    return null;
  }
  const t12 = `Alert Alert--${variant} relative flex items-start justify-between`;
  let t13;
  if ($[13] !== className || $[14] !== t12) {
    t13 = cn(t12, className);
    $[13] = className;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== message || $[17] !== title || $[18] !== variant) {
    t14 = variant === "negative" && <TrackError errorMessage={title ? title.toString() : message?.toString()} />;
    $[16] = message;
    $[17] = title;
    $[18] = variant;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== dismissible || $[21] !== hasCloseButton || $[22] !== mobileCentered) {
    t15 = cn("w-full p-comfy-md flex gap-comfy-xs items-center", {
      Alert__wrapper: dismissible,
      "pr-snug-xl": hasCloseButton,
      "flex-col md:flex-row text-center md:text-left": mobileCentered
    });
    $[20] = dismissible;
    $[21] = hasCloseButton;
    $[22] = mobileCentered;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== mobileCentered || $[25] !== noIcon || $[26] !== variant) {
    t16 = !noIcon && <div className={cn("Alert__icon md:align-start", {
      "align-start": !mobileCentered
    })}>{{
        neutral: <ArkIcon iconName="info-circle" />,
        positive: <ArkIcon iconName="check-circle" />,
        warning: <ArkIcon iconName="alert-triangle" />,
        negative: <ArkIcon iconName="alert-circle" />,
        brand: <ArkIcon iconName="info-circle" />
      }[variant]}</div>;
    $[24] = mobileCentered;
    $[25] = noIcon;
    $[26] = variant;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = !middleColumn;
  let t18;
  if ($[28] !== t17) {
    t18 = cn("Alert__content items-center gap-comfy-sm justify-between", {
      "grow-1": t17
    });
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== title) {
    t19 = title && <div className="Alert__title label-md label-potent label-neutral-amplified">{title}</div>;
    $[30] = title;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== message) {
    t20 = message && <div className="Alert__message label-md label-moderate mt-snug-md">{message}</div>;
    $[32] = message;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== footer) {
    t21 = footer && <div className="mt-comfy-sm">{footer}</div>;
    $[34] = footer;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== action || $[37] !== dismissible || $[38] !== mobileCentered || $[39] !== onClose || $[40] !== t) {
    t22 = dismissible && <div className={cn("Alert__btn-close mt-comfy-sm md:mr-comfy-sm", {
      "mr-comfy-sm": !mobileCentered || action
    })}><LinkStyle purpose="neutral" noUnderline={true} noPadding={true}><button type="button" onClick={() => alertDismiss(setDismissed, onClose)}>{t("core:actions.dismiss")}</button></LinkStyle></div>;
    $[36] = action;
    $[37] = dismissible;
    $[38] = mobileCentered;
    $[39] = onClose;
    $[40] = t;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== action) {
    t23 = action && <div className="Alert__btn-action mt-comfy-sm mr-comfy-sm"><LinkStyle noUnderline={true} noPadding={true}>{action}</LinkStyle></div>;
    $[42] = action;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t18 || $[45] !== t19 || $[46] !== t20 || $[47] !== t21 || $[48] !== t22 || $[49] !== t23) {
    t24 = <div className={t18}>{t19}{t20}{t21}{t22}{t23}</div>;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== middleColumn) {
    t25 = middleColumn && <div className="grow-1">{middleColumn}</div>;
    $[51] = middleColumn;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== ControlledTrigger || $[54] !== styledButtonAction) {
    t26 = styledButtonAction && (ControlledTrigger ? <ControlledTrigger>{styledButtonAction}</ControlledTrigger> : styledButtonAction);
    $[53] = ControlledTrigger;
    $[54] = styledButtonAction;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t15 || $[57] !== t16 || $[58] !== t24 || $[59] !== t25 || $[60] !== t26) {
    t27 = <div className={t15}>{t16}{t24}{t25}{t26}</div>;
    $[56] = t15;
    $[57] = t16;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== hasCloseButton || $[63] !== onClose || $[64] !== t) {
    t28 = hasCloseButton && <CloseButton className="mt-snug-xl mr-snug-xl" onClick={() => alertDismiss(setDismissed, onClose)} aria-label={t("core:actions.dismiss")} />;
    $[62] = hasCloseButton;
    $[63] = onClose;
    $[64] = t;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t13 || $[67] !== t14 || $[68] !== t27 || $[69] !== t28) {
    t29 = <div ref={alertRef} className={t13}>{t14}{t27}{t28}</div>;
    $[66] = t13;
    $[67] = t14;
    $[68] = t27;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  return t29;
};